import { CommonModule } from '@angular/common';
import { Component, Input, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked';
import {
  AiNarrationActionItemGroup,
  AiNarrationAssetRow,
  AiNarrationRiskItem,
  AiNarrationSection,
  AiTaskContextBlock,
  TaskContextSectionBlock,
} from '../../models/ai-assistant.models';

interface ColumnDef {
  key: string;
  label: string;
}

const SECTION_COLUMNS: Record<string, ColumnDef[]> = {
  action_items: [
    { key: 'id', label: 'Action Item' },
    { key: 'assetName', label: 'Asset' },
    { key: 'status', label: 'Status' },
  ],
  assets: [
    { key: 'name', label: 'Name' },
    { key: 'assetType', label: 'Type' },
    { key: 'assetCategory', label: 'Category' },
    { key: 'criticality', label: 'Criticality' },
  ],
  associated_risks: [
    { key: 'riskId', label: 'Risk ID' },
    { key: 'title', label: 'Title' },
    { key: 'riskLevel', label: 'Risk Level' },
    { key: 'status', label: 'Status' },
  ],
  frameworks: [
    { key: 'name', label: 'Framework' },
  ],
};

@Component({
  selector: 'app-response-task-context',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './response-task-context.component.html',
  styleUrl: './response-task-context.component.css',
})
export class ResponseTaskContextComponent {
  @Input({ required: true }) block!: AiTaskContextBlock;

  private readonly sanitizer = inject(DomSanitizer);

  // ── Contract-driven path ───────────────────────────────────────────────────

  get hasSectionBlocks(): boolean {
    return !!(this.block.sectionBlocks?.length);
  }

  get sectionBlocks(): TaskContextSectionBlock[] {
    return this.block.sectionBlocks ?? [];
  }

  sectionProseHtml(section: TaskContextSectionBlock): SafeHtml {
    const html = marked(section.prose, { async: false }) as string;
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  columns(section: TaskContextSectionBlock): ColumnDef[] {
    return SECTION_COLUMNS[section.key] ?? [];
  }

  cellValue(row: Record<string, unknown>, key: string): string {
    const v = row[key];
    if (v === null || v === undefined || v === '') return '—';
    if (Array.isArray(v)) return v.join(', ');
    return String(v);
  }

  isBadgeColumn(columnKey: string): boolean {
    return columnKey === 'criticality' || columnKey === 'riskLevel' || columnKey === 'priority' || columnKey === 'status';
  }

  badgeClass(value: string | undefined): string {
    return `ai-pill tone-${(value ?? '').toLowerCase().replace(/\s+/g, '-')}`;
  }

  // ── Legacy UNI path ────────────────────────────────────────────────────────

  get sections(): AiNarrationSection[] {
    return this.block.sections ?? [];
  }

  factsSection(key: string): AiNarrationSection | undefined {
    return this.sections.find((s) => s.key === key);
  }

  actionItemGroups(section: AiNarrationSection): AiNarrationActionItemGroup[] {
    return (section.actionItemGroups ?? []) as AiNarrationActionItemGroup[];
  }

  topAssets(section: AiNarrationSection): AiNarrationAssetRow[] {
    return (section.assetSummary?.topAssets ?? []) as AiNarrationAssetRow[];
  }

  riskItems(section: AiNarrationSection): AiNarrationRiskItem[] {
    return (section.items ?? []) as unknown as AiNarrationRiskItem[];
  }

  nextStepItems(section: AiNarrationSection): { message: string; priority: string }[] {
    return (section.items ?? []) as unknown as { message: string; priority: string }[];
  }

  priorityClass(value: string | undefined): string {
    return this.badgeClass(value);
  }
}
